import React, { useState } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";
import { generateRectificationRequestPDF } from "../generateRectificationRequestPDF";
import { generateServiceRequestPDF } from "../generateServiceRequestPDF";

// ── Shared modal shell ────────────────────────────────────────────────────────

function ModalShell({ onClose, children, isMobile }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 399 }}
      />
      <div
        style={
          isMobile
            ? { position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: colors.white, borderRadius: "16px 16px 0 0", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)", padding: "20px", zIndex: 400, maxHeight: "90vh", overflowY: "auto" }
            : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: colors.white, borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", padding: "28px", zIndex: 400, width: "500px", maxHeight: "88vh", overflowY: "auto" }
        }
      >
        {isMobile && <div style={{ width: "40px", height: "4px", borderRadius: "2px", backgroundColor: colors.border, margin: "0 auto 16px" }} />}
        {children}
      </div>
    </>
  );
}

// ── Photo preview helper ──────────────────────────────────────────────────────

function PhotoInput({ file, onChange, label = "Add Photo (optional)" }) {
  return (
    <div>
      <label style={{ fontSize: "12px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
        {label}
      </label>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => onChange(e.target.files[0] || null)}
        style={{ fontSize: "13px", color: colors.darkGrey }}
      />
      {file && (
        <img
          src={URL.createObjectURL(file)}
          alt="preview"
          style={{ marginTop: "8px", width: "100%", maxHeight: "180px", objectFit: "cover", borderRadius: "8px", border: `1px solid ${colors.border}` }}
        />
      )}
    </div>
  );
}

// ── Silent file function for rectification request completion PDF ─────────────

const _toDataURL = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width; c.height = img.height;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png").split(",")[1]);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

async function silentFileRectificationRequestPDF(requestData) {
  const signaturesMap = {};
  try {
    const sigsRes = await axios.get(`${API}/tech-signatures/`);
    for (const s of sigsRes.data) {
      if (s.signature_url) signaturesMap[s.tech_name] = await _toDataURL(s.signature_url);
    }
  } catch {}
  const blob = await generateRectificationRequestPDF(requestData, signaturesMap, { noDownload: true });
  const year = requestData.quote_detail?.inspection_date
    ? new Date(requestData.quote_detail.inspection_date).getFullYear()
    : new Date().getFullYear();
  const typeLabel = { annual: "Annual", bi_annual: "Bi-Annual", monthly: "Monthly" }[requestData.quote_detail?.inspection_type] || "Inspection";
  const folderName = `${typeLabel} ${year}`;
  const siteId = requestData.quote_detail?.inspection_site_id;
  if (!siteId) throw new Error("No site ID on request — cannot file report");
  const foldersRes = await axios.get(`${API}/site-folders/?site=${siteId}`);
  let folder = foldersRes.data.find((f) => f.name === folderName);
  if (!folder) {
    const cr = await axios.post(`${API}/site-folders/`, { site: siteId, name: folderName, is_system: true });
    folder = cr.data;
  }
  const siteName = (requestData.quote_detail?.inspection_site_name || "site").replace(/[^a-z0-9]/gi, "_");
  const fileName = `RR-${String(requestData.id).padStart(3, "0")}_${siteName}_Rectification_Report.pdf`;
  const existing = (folder.documents || []).find((d) => d.name === fileName);
  if (existing) await axios.delete(`${API}/site-documents/${existing.id}/`);
  const pdfFile = new File([blob], fileName, { type: "application/pdf" });
  const fd = new FormData();
  fd.append("file", pdfFile);
  fd.append("folder", folder.id);
  await axios.post(`${API}/site-documents/`, fd);
}

async function silentFileSRPDF(srData) {
  const signaturesMap = {};
  try {
    const sigsRes = await axios.get(`${API}/tech-signatures/`);
    for (const s of sigsRes.data) {
      if (s.signature_url) signaturesMap[s.tech_name] = await _toDataURL(s.signature_url);
    }
  } catch {}
  const blob = await generateServiceRequestPDF(srData, signaturesMap, { noDownload: true });
  const year = srData.completed_at
    ? new Date(srData.completed_at).getFullYear()
    : new Date().getFullYear();
  const folderName = `Service Requests ${year}`;
  const siteId = srData.site;
  if (!siteId) throw new Error("No site ID on service request — cannot file report");
  const foldersRes = await axios.get(`${API}/site-folders/?site=${siteId}`);
  let folder = foldersRes.data.find((f) => f.name === folderName);
  if (!folder) {
    const cr = await axios.post(`${API}/site-folders/`, { site: siteId, name: folderName, is_system: true });
    folder = cr.data;
  }
  const siteName = (srData.site_name || "site").replace(/[^a-z0-9]/gi, "_");
  const fileName = `SR-${String(srData.id).padStart(3, "0")}_${siteName}_Service_Request_Report.pdf`;
  const existing = (folder.documents || []).find((d) => d.name === fileName);
  if (existing) await axios.delete(`${API}/site-documents/${existing.id}/`);
  const pdfFile = new File([blob], fileName, { type: "application/pdf" });
  const fd = new FormData();
  fd.append("file", pdfFile);
  fd.append("folder", folder.id);
  await axios.post(`${API}/site-documents/`, fd);
}

// ── ServiceRequestWorkModal ───────────────────────────────────────────────────

export function ServiceRequestWorkModal({ job, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const [actionsTaken, setActionsTaken] = useState(job.raw?.actions_taken || "");
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const sr = job.raw || {};

  const handleSave = async () => {
    if (!actionsTaken.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("actions_taken", actionsTaken.trim());
      fd.append("status", "complete");
      if (photo) fd.append("photo", photo);
      const patchRes = await axios.patch(`${API}/service-requests/${sr.id}/`, fd);
      try {
        await silentFileSRPDF(patchRes.data);
      } catch (pdfErr) {
        alert(
          "Work saved ✓, but report filing failed:\n" +
          (pdfErr?.response?.data ? JSON.stringify(pdfErr.response.data) : pdfErr?.message)
        );
      }
      onSaved();
      onClose();
    } catch (e) {
      alert("Save failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: "12px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", display: "block", marginBottom: "6px" };
  const fieldBox = { marginBottom: "16px" };

  return (
    <ModalShell onClose={onClose} isMobile={isMobile}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
        <div>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: colors.amber, marginBottom: "4px" }}>
            🔧 Service Request
          </div>
          <div style={{ fontSize: "17px", fontWeight: "700", color: colors.darkGrey }}>{job.site_name}</div>
          <div style={{ fontSize: "13px", color: colors.slate }}>{job.site_address}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate }}>✕</button>
      </div>

      {/* Request description */}
      {sr.service_request && (
        <div style={{ ...fieldBox, padding: "10px", backgroundColor: "#fff7ed", borderRadius: "8px", border: `1px solid ${colors.amber}` }}>
          <div style={{ fontSize: "11px", fontWeight: "700", color: colors.amber, marginBottom: "4px" }}>REQUEST</div>
          <div style={{ fontSize: "13px", color: colors.darkGrey }}>{sr.service_request}</div>
        </div>
      )}

      {/* Actions taken */}
      <div style={fieldBox}>
        <label style={labelStyle}>Actions Taken *</label>
        <textarea
          value={actionsTaken}
          onChange={(e) => setActionsTaken(e.target.value)}
          placeholder="Describe what was done..."
          rows={4}
          style={{ width: "100%", padding: "10px", border: `1px solid ${colors.border}`, borderRadius: "8px", fontSize: "13px", color: colors.darkGrey, resize: "vertical", boxSizing: "border-box" }}
        />
      </div>

      {/* Photo */}
      <div style={fieldBox}>
        <PhotoInput file={photo} onChange={setPhoto} />
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={saving || !actionsTaken.trim()}
        style={{ width: "100%", padding: "13px", borderRadius: "8px", border: "none", backgroundColor: colors.amber, color: "white", fontSize: "14px", fontWeight: "700", cursor: saving || !actionsTaken.trim() ? "not-allowed" : "pointer", opacity: saving || !actionsTaken.trim() ? 0.6 : 1 }}
      >
        {saving ? "Saving…" : sr.status === "complete" ? "✓ Save & Update Report" : "✓ Mark Complete"}
      </button>
    </ModalShell>
  );
}

// ── RectificationWorkModal ────────────────────────────────────────────────────

export function RectificationWorkModal({ job, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const rr = job.raw || {};
  const rrItems = rr.items || [];

  const initForms = () => {
    const obj = {};
    rrItems.forEach((item) => {
      obj[item.id] = {
        actions_taken: item.actions_taken || "",
        photoFile: null,
        attested: item.attested || false,
      };
    });
    return obj;
  };

  const [itemForms, setItemForms] = useState(initForms);
  const [saving, setSaving] = useState(false);

  const updateItem = (id, key, value) =>
    setItemForms((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const attestedCount = Object.values(itemForms).filter((f) => f.attested).length;
  const allFilled = rrItems.every((item) => (itemForms[item.id]?.actions_taken || "").trim());

  const handleSave = async (markAllAttested = false) => {
    setSaving(true);
    try {
      for (const item of rrItems) {
        const form = itemForms[item.id] || {};
        const fd = new FormData();
        fd.append("actions_taken", (form.actions_taken || "").trim());
        fd.append("attested", markAllAttested ? "true" : String(form.attested));
        if (form.photoFile) fd.append("photo", form.photoFile);
        await axios.patch(`${API}/rectification-request-items/${item.id}/`, fd);
      }
      const freshRes = await axios.get(`${API}/rectification-requests/${rr.id}/`);
      if (freshRes.data.status === "completed") {
        try {
          await silentFileRectificationRequestPDF(freshRes.data);
        } catch (pdfErr) {
          alert(
            "Work saved ✓, but report filing failed:\n" +
            (pdfErr?.response?.data ? JSON.stringify(pdfErr.response.data) : pdfErr?.message)
          );
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      alert("Save failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: "12px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", display: "block", marginBottom: "6px" };

  return (
    <ModalShell onClose={onClose} isMobile={isMobile}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
        <div>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: colors.red, marginBottom: "4px" }}>
            🔨 Rectification Work
          </div>
          <div style={{ fontSize: "17px", fontWeight: "700", color: colors.darkGrey }}>{job.site_name}</div>
          <div style={{ fontSize: "13px", color: colors.slate }}>{job.site_address}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate }}>✕</button>
      </div>

      {/* Progress indicator */}
      <div style={{ backgroundColor: "#fdecea", borderRadius: "8px", padding: "8px 12px", marginBottom: "16px", fontSize: "12px", fontWeight: "700", color: colors.red }}>
        {attestedCount} / {rrItems.length} items attested
      </div>

      {/* Item cards */}
      {rrItems.map((item, i) => {
        const qi = item.quote_item_detail || {};
        const d = qi.inspection_item_detail || {};
        const defectPhotos = [d.photo_1_url, d.photo_2_url, d.photo_3_url].filter(Boolean);
        const form = itemForms[item.id] || {};

        return (
          <div key={item.id} style={{ marginBottom: "16px", padding: "14px", border: `1px solid ${colors.border}`, borderRadius: "10px", backgroundColor: form.attested ? "#f0fdf4" : colors.white }}>
            {/* Defect reference */}
            <div style={{ fontSize: "13px", fontWeight: "700", color: colors.darkGrey, marginBottom: "4px" }}>
              {i + 1}. {d.asset_type_name || "Asset"}
              {(d.floor_level || d.location) && (
                <span style={{ fontWeight: "400", color: colors.slate }}>
                  {" — "}{[d.floor_level, d.location].filter(Boolean).join(", ")}
                </span>
              )}
            </div>
            {d.notes && (
              <div style={{ fontSize: "12px", color: colors.red, marginBottom: "6px" }}>
                Defect: {d.notes}
              </div>
            )}
            {qi.proposed_solution && (
              <div style={{ fontSize: "12px", color: colors.teal, marginBottom: "8px" }}>
                ✅ Recommended: {qi.proposed_solution}
              </div>
            )}
            {defectPhotos.length > 0 && (
              <div style={{ display: "flex", gap: "6px", marginBottom: "10px", flexWrap: "wrap" }}>
                {defectPhotos.map((url, pi) => (
                  <img key={pi} src={url} alt="defect" style={{ width: "72px", height: "54px", objectFit: "cover", borderRadius: "4px", border: `1px solid ${colors.border}` }} />
                ))}
              </div>
            )}

            {/* Actions taken */}
            <div style={{ marginBottom: "10px" }}>
              <label style={labelStyle}>Actions Taken</label>
              <textarea
                value={form.actions_taken}
                onChange={(e) => updateItem(item.id, "actions_taken", e.target.value)}
                placeholder="Describe what was done for this item..."
                rows={3}
                style={{ width: "100%", padding: "8px", border: `1px solid ${colors.border}`, borderRadius: "6px", fontSize: "13px", color: colors.darkGrey, resize: "vertical", boxSizing: "border-box" }}
              />
            </div>

            {/* Completion photo */}
            <div style={{ marginBottom: "10px" }}>
              <PhotoInput file={form.photoFile} onChange={(f) => updateItem(item.id, "photoFile", f)} label="Completion Photo (optional)" />
            </div>

            {/* Attest checkbox */}
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600", color: form.attested ? "#15803d" : colors.darkGrey }}>
              <input
                type="checkbox"
                checked={form.attested}
                onChange={(e) => updateItem(item.id, "attested", e.target.checked)}
                style={{ width: "16px", height: "16px", accentColor: "#15803d" }}
              />
              Attest — works completed for this item
            </label>
          </div>
        );
      })}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: "10px" }}>
        <button
          onClick={() => handleSave(false)}
          disabled={saving}
          style={{ flex: 1, padding: "12px", borderRadius: "8px", border: `1px solid ${colors.border}`, backgroundColor: colors.white, color: colors.darkGrey, fontSize: "13px", fontWeight: "600", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Save Progress"}
        </button>
        <button
          onClick={() => handleSave(true)}
          disabled={saving || !allFilled}
          style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "none", backgroundColor: colors.red, color: "white", fontSize: "13px", fontWeight: "700", cursor: saving || !allFilled ? "not-allowed" : "pointer", opacity: saving || !allFilled ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Save & Complete"}
        </button>
      </div>
      {!allFilled && (
        <div style={{ fontSize: "11px", color: colors.slate, textAlign: "center", marginTop: "6px" }}>
          Fill in actions taken for all items to complete
        </div>
      )}
    </ModalShell>
  );
}
